/** @format */
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import React, {
    Fragment,
    FunctionComponent,
    Key,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from 'react';
import styled from 'styled-components';

import { normalizeIsoCode } from '@toucan/common/helpers/language';
import {
    CategoryNavigationType,
    getTaxonomyNavigation,
} from '@toucan/common/services/navigation/helpers/navigation';

import {
    ProductCategory,
    TaxonomyContext,
} from '@toucan/client/global/application';
import { MarketCode, MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';

import { getFilteredBrandsByShortList } from '@toucan/client/core/brand';
import {
    OriginalContentNavItem,
    getNavOriginalContent,
} from '@toucan/client/core/contentful';

import { GridRepeater } from '@toucan/client/shared/components/grid-repeater';
import {
    Link,
    LinkColor,
    LinkSize,
    LinkVariant,
} from '@toucan/client/shared/components/link';
import {
    TabContent,
    TabPanel,
    TabProps,
} from '@toucan/client/shared/components/tab-panel';

import {
    baseTabStyles,
    categoryTabStyles,
    styles,
} from './desktop-main-nav.styles';

type PopularBrand = {
    name: string;
    id: number;
    logo: ReactNode;
};

export enum DesktopMainNavTabVariant {
    CATEGORIES,
    BRANDS,
}

export type TaxonomyTabProps = {
    categories: ProductCategory[];
    initialDepth?: number;
};

export type BrandTabProps = {
    brands: PopularBrand[];
};

export type DesktopMainNavProps = {
    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
};

type WithActiveTabAndBrands = {
    brands: PopularBrand[];
};

export type DesktopMainNavPropsWithActiveTab = DesktopMainNavProps &
    WithActiveTabAndBrands;

export type BaseTabProps = {
    isActive: boolean;
};

const StyledDesktopMainNav = styled.nav`
    ${styles}
`;

const StyledTab = styled.div<BaseTabProps>`
    ${baseTabStyles}
`;

const StyledTaxonomyTab = styled.div<BaseTabProps>`
    ${baseTabStyles}
    ${categoryTabStyles}
`;

/**
 * Renders the CategoriesTab
 *
 * @param {CategoriesTabProps} - The properties required by the CategoriesTab component
 * @returns {ReactElement} - The brand tab
 */
const TaxonomyTab: FunctionComponent<TaxonomyTabProps> = ({
    categories,
    initialDepth,
}: TaxonomyTabProps) => {
    function renderCategories(
        categories: ProductCategory[],
        depth: number = 0
    ): ReactNode {
        return categories.map(
            (category: ProductCategory, index: number): ReactNode => {
                const linkSize =
                    depth === 0 // eslint-disable-line no-nested-ternary
                        ? LinkSize.LARGE
                        : depth === 1
                        ? LinkSize.MEDIUM
                        : LinkSize.SMALL;

                return (
                    <ul
                        className="category-tree"
                        key={`category-tree--${index}`}
                        data-depth={depth}
                    >
                        <li className="category">
                            <NextLink
                                prefetch={false}
                                href={category.templatePath}
                                as={category.url}
                                passHref={true}
                                legacyBehavior
                            >
                                <Link
                                    href={category.url}
                                    size={linkSize}
                                    linkColor={LinkColor.NAVIGATION}
                                    role="menuitem"
                                >
                                    {category.title}
                                </Link>
                            </NextLink>

                            {category.categories &&
                                renderCategories(
                                    category.categories,
                                    depth + 1
                                )}
                        </li>
                    </ul>
                );
            }
        );
    }

    return (
        <ul className="columns">
            <li>{renderCategories(categories, initialDepth)}</li>
        </ul>
    );
};

const BrandTaxonomyTab = ({ categories, initialDepth }: TaxonomyTabProps) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const searchSlug = t('search', { ns: 'urls' });

    const browseAllLinkProps =
        activeMarket.code === MarketCode.FR_FR
            ? {
                  href: '/search?q=*',
                  as: `/${activeMarket.slug}/${searchSlug}?q=*`,
              }
            : {
                  href: '/brands',
                  as: `/${activeMarket.slug}/${t('brands', { ns: 'urls' })}`,
              };

    return (
        <Fragment>
            <TaxonomyTab categories={categories} initialDepth={initialDepth} />
            <WrappedLink
                {...browseAllLinkProps}
                className="browse-all-link"
                data-testid="desktop-menu__browse-all-link"
            >
                {t('Browse all brands')}
            </WrappedLink>
        </Fragment>
    );
};

const StyledOriginalContentLink = styled(Link)`
    small {
        color: var(--colors-text-primary);
        font-size: 1.2rem;
        line-height: 1.8rem;
        font-weight: 300;
    }
`;

type OriginalContentLinkProps = {
    onClick?: () => null;
    href?: string;
    name: ReactNode;
    teaser: ReactNode;
};

const OriginalContentLink = ({
    name,
    teaser,
    onClick,
    href,
}: OriginalContentLinkProps) => (
    <StyledOriginalContentLink
        variant={LinkVariant.CUSTOM}
        href={href}
        onClick={onClick}
    >
        <Link linkColor={LinkColor.NAVIGATION}>{name}</Link>
        <br />
        <small>{teaser}</small>
    </StyledOriginalContentLink>
);

type GenerateContentLinkArgs = {
    name: ReactNode;
    as?: string;
    disabled?: boolean;
    teaser: ReactNode;
    key: Key;
    templatePath: string;
};

const OriginalContentTab: FunctionComponent = () => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    // Null value is used as a value to show skeletons whilst data is loading
    const [contentfulData, setContentfulData] = useState<
        OriginalContentNavItem[] | null[]
    >(new Array(6).fill(null));

    const contentSlug = t('content', { ns: 'urls' });

    function isLoading(
        data: OriginalContentNavItem[] | null[]
    ): data is null[] {
        return data[0] === null;
    }

    function generateContentLink({
        as,
        disabled,
        teaser,
        name,
        key,
        templatePath,
    }: GenerateContentLinkArgs) {
        return (
            <NextLink
                prefetch={false}
                href={templatePath}
                as={as}
                passHref={true}
                key={key}
                legacyBehavior
            >
                <OriginalContentLink name={name} teaser={teaser} />
            </NextLink>
        );
    }

    function generateSkeletonContent(skeletons: null[]) {
        return skeletons.map((x, key) => {
            return generateContentLink({
                key,
                disabled: true,
                templatePath: '/content/category',
                name: (
                    <Skeleton
                        variant={SkeletonVariant.TEXT}
                        className="original-content-nav-item-title"
                    />
                ),
                teaser: (
                    <Skeleton variant={SkeletonVariant.TEXT} lineCount={3} />
                ),
            });
        });
    }

    function generateContentfulContent(navItems: OriginalContentNavItem[]) {
        return navItems.map(({ urlSlug, name, teaser }) => {
            return generateContentLink({
                name,
                teaser,
                key: urlSlug,
                templatePath: `/content/category?slug=${urlSlug}`,
                as: `/${activeMarket.slug}/${contentSlug}/${urlSlug}/`,
            });
        });
    }

    useEffect(() => {
        let isCancelled = false;
        (async () => {
            try {
                const data = await getNavOriginalContent(
                    normalizeIsoCode(activeMarket.iso)
                );

                if (isCancelled) {
                    return;
                }

                setContentfulData(data);
            } catch (error) {
                console.error(error);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, []);

    return (
        <Fragment>
            <GridRepeater gridColumns={2}>
                {isLoading(contentfulData)
                    ? generateSkeletonContent(contentfulData)
                    : generateContentfulContent(contentfulData)}
            </GridRepeater>
            <WrappedLink
                className="see-all-original-content-link"
                href="/content"
                as={`/${activeMarket.slug}/${contentSlug}`}
            >
                {t('See all original content')}
            </WrappedLink>
        </Fragment>
    );
};

export const DesktopMainNav: FunctionComponent<DesktopMainNavProps> = ({
    ...otherProps
}: DesktopMainNavProps) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const { categories: categoriesTaxonomy, brands: brandsTaxonomy } =
        useContext(TaxonomyContext);

    const tabs: TabProps[] = [
        {
            children: t('Shop By Category'),
            'data-testid': 'desktop-menu__categories-tab-button',
        },
        {
            children: t('Shop By Brand'),
            'data-testid': 'desktop-menu__brands-tab-button',
        },
        {
            children: t('Original content'),
            'data-testid': 'desktop-menu__content-tab-button',
        },
    ];

    const categories = getTaxonomyNavigation(
        categoriesTaxonomy,
        activeMarket,
        t,
        CategoryNavigationType.CATEGORY
    );

    const brands = getTaxonomyNavigation(
        getFilteredBrandsByShortList(brandsTaxonomy),
        activeMarket,
        t,
        CategoryNavigationType.BRAND
    );

    function renderTabContent({ activeTabIndex }: TabContent): ReactNode {
        return (
            <Fragment>
                <StyledTaxonomyTab
                    isActive={activeTabIndex === 0}
                    data-testid="desktop-menu__categories-tab"
                >
                    {categories && <TaxonomyTab categories={categories} />}
                </StyledTaxonomyTab>
                <StyledTaxonomyTab
                    isActive={activeTabIndex === 1}
                    data-testid="desktop-menu__brands-tab"
                >
                    {brands && (
                        <BrandTaxonomyTab
                            categories={brands}
                            initialDepth={1}
                        />
                    )}
                </StyledTaxonomyTab>
                <StyledTab
                    isActive={activeTabIndex === 2}
                    data-testid="desktop-menu__content-tab"
                >
                    <OriginalContentTab />
                </StyledTab>
            </Fragment>
        );
    }

    return (
        <StyledDesktopMainNav {...otherProps}>
            <TabPanel
                tabs={tabs}
                className="menu-tabs"
                data-testid="desktop-menu__tabs"
            >
                {renderTabContent}
            </TabPanel>
        </StyledDesktopMainNav>
    );
};
